<script setup lang="ts">
import CoreIcon from '@core/core-icon.vue';
import PartProgressBar from '@parts/part-progress-bar.vue';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useBytes } from '@composables/bytes';
import { useDates } from '@composables/dates';
import { useReserveStore } from '@store/reserve';

/**
 * Вход в «Резервное подключение» с главной и из кабинета.
 *
 * Показывается только когда резерв выдан (кошелёк бота в состоянии active или
 * grace) — продажи резерва при этом могут быть выключены: бета-доступ раздаётся
 * из админки бота и должен быть виден и без продаж.
 *
 * Слева под названием — что будет дальше («Обновится 8 октября» или
 * «Закончится …»), справа — «0 из 10 ГБ» потраченного, ниже полоса расхода.
 * Состояние берётся из общего стора: страница уже его загрузила, второй
 * запрос не нужен.
 */
const { t } = useI18n();
const reserve = useReserveStore();
const { formatTraffic, formatTrafficPair } = useBytes();
const { formatDate, daysUntil } = useDates();

// Оранжевым — только когда конец близко: при помесячной оплате «Закончится»
// стоит почти всегда, и постоянный оранжевый перестал бы читаться как тревога
const ALARM_DAYS = 7;

// «0 из 10 ГБ» — потрачено из выданного; без подписки справа пусто, всё
// сказано подписью слева; без счётчиков — «нет данных»
const usedText = computed((): string => {
	if (reserve.isInactive) return '';
	if (!reserve.remaining) return t('reserve.no_data');
	return formatTrafficPair(reserve.grantedBytes - reserve.remainingBytes, reserve.grantedBytes);
});

const usedLabel = computed((): string => {
	if (reserve.isInactive) return usedText.value;
	if (!reserve.remaining) return t('reserve.no_data');
	return t('reserve.used', { used: usedText.value });
});

// «Обновится 8 октября» / «Закончится 20 сентября». Без подписки — призыв
// «Сохраните 90 ГБ», пока оплаченные хранятся, иначе «Подписка неактивна»
const nextText = computed((): string => {
	if (reserve.isInactive) {
		return reserve.savedBytes > 0
			? t('reserve.keep', { n: formatTraffic(reserve.savedBytes) })
			: t('reserve.state_inactive');
	}
	const event = reserve.nextEvent;
	if (!event) return '';
	const date = formatDate(event.at, 'dayMonth');
	if (!date) return '';
	return t(event.kind === 'ends' ? 'reserve.ends_on' : 'reserve.renews_on', { date });
});

const isEndingSoon = computed((): boolean => {
	const event = reserve.nextEvent;
	if (!event || event.kind !== 'ends') return false;
	const days = daysUntil(event.at);
	return days !== null && days <= ALARM_DAYS;
});
</script>

<template>
	<router-link
		v-if="reserve.isVisible"
		to="/account/reserve"
		class="part-reserve-card"
	>
		<span class="part-reserve-card__icon">
			<core-icon name="signal" viewBox="0 0 18 18" />
		</span>
		<span class="part-reserve-card__text">
			<span class="part-reserve-card__title">{{ t('reserve.title') }}</span>
			<span class="part-reserve-card__row">
				<span
					class="part-reserve-card__subtitle"
					:class="{ 'part-reserve-card__subtitle_alarming': isEndingSoon }"
				>
					{{ nextText }}
				</span>
				<span
					v-if="usedText"
					class="part-reserve-card__value"
				>{{ usedText }}</span>
			</span>
			<part-progress-bar
				v-if="!reserve.isInactive"
				:percent="reserve.usedPercent"
				:label="usedLabel"
				class="part-reserve-card__progress"
			/>
			<span
				v-if="reserve.isExhausted && !reserve.isInactive"
				class="part-reserve-card__note"
			>
				{{ t('reserve.limited') }}
			</span>
		</span>
		<core-icon
			name="arrowLeft"
			viewBox="0 0 13 22"
			class="part-reserve-card__arrow"
		/>
	</router-link>
</template>

<style lang="scss">
.part-reserve-card {
	display: flex;
	align-items: center;
	gap: 1.6rem;
	padding: 1.2rem 1.6rem;
	min-height: 5.2rem;
	border-radius: 1.6rem;
	background-color: var(--color-panel);
	border: 1px solid var(--color-panel);
	color: var(--color-text);
	fill: var(--color-active);
	transition: background-color 0.12s;

	@include min-desktop {
		min-height: 6.2rem;
	}

	// Подсветка там, где есть курсор, как у пунктов меню рядом
	@media (hover: hover) and (pointer: fine) {
		&:hover {
			background-color: rgba(var(--color-active-rgb), 0.10);
			color: var(--color-text);

			.part-reserve-card__arrow {
				transform: rotate(-180deg) translateX(-0.5rem);
			}
		}
	}

	&__icon {
		flex: 0 0 auto;
		width: 3.2rem;
		height: 3.2rem;
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 50%;
		background: var(--color-white);

		@include theme-dark {
			background-color: var(--color-panel);
			box-shadow: inset 1px 1px 1px 0 var(--color-panel);
		}

		svg {
			width: 1.6rem;
		}
	}

	&__text {
		flex: 1 1 auto;
		min-width: 0;
		display: flex;
		flex-direction: column;
		gap: 0.4rem;
	}

	&__title {
		@include min-desktop {
			@include font-18-pro-medium;
		}
	}

	// Подпись слева, потраченное справа — над полосой, у её конца
	&__row {
		display: flex;
		align-items: baseline;
		justify-content: space-between;
		gap: 1.2rem;
	}

	&__subtitle {
		@include font-12-pro-regular;
		opacity: 0.7;

		&_alarming {
			opacity: 1;
			color: var(--color-orange);

			@include theme-dark {
				color: #FFAD9B;
			}
		}
	}

	&__value {
		flex: 0 0 auto;
		@include font-12-pro-regular;
		opacity: 0.7;
	}

	&__progress {
		margin-top: 0.2rem;
	}

	&__note {
		@include font-12-pro-regular;
		color: var(--color-orange);

		@include theme-dark {
			color: #FFAD9B;
		}
	}

	&__arrow {
		flex: 0 0 auto;
		width: 0.7rem;
		transform: rotate(-180deg);
		margin: 0 0.6rem;
		transition: transform 0.12s;

		@include min-desktop {
			width: 0.8rem;
		}
	}
}
</style>
